<template>
  <section class="mi-home-app">
    <Heading
      tag="h2"
      size="lg"
      text-uppercase
      font-weight="ultra"
      align="center"
      spaces-bottom
      class="mi-home-app__heading"
    >
      {{ content.heading }}
    </Heading>
    <Paragraph
      class="mi-home-app__copy"
      align="center"
      >{{ content.copy }}</Paragraph
    >
    <div class="mi-home-app__icons-spread-wrapper">
      <div class="mi-home-app__icons-container">
        <CdnImage
          :src="content.miHomeIcon"
          lazy
          class="mi-home-app__mi-home-icon"
          render-without-figure
          alt="Mi Home App Icon"
        />
        <CdnImage
          :src="content.iconSpread.mobile"
          lazy
          class="mi-home-app__icon-spread mi-home-app__icon-spread--mobile"
          render-without-figure
          alt="Vertikale grüne Linie mit horizontalen Segmenten."
        />
        <CdnImage
          :src="content.iconSpread.desktop"
          lazy
          class="mi-home-app__icon-spread mi-home-app__icon-spread--desktop"
          render-without-figure
          alt="Horizontale grüne Linie mit vertikalen Segmenten."
        />
        <div
          v-for="icon in content.icons"
          :key="icon.copy"
          class="mi-home-app__icon-wrapper"
        >
          <CdnImage
            :src="icon.src"
            lazy
            class="mi-home-app__icon"
            :class="`mi-home-app__icon--${icon.id}`"
            render-without-figure
            :alt="`Icon - ${icon.copy}`"
          />
          <div>{{ icon.copy }}</div>
        </div>
      </div>
      <LinkButton
        tag="nuxt-link"
        to="/xiaomi-zubehoer"
        class="mi-home-app__link"
        outline
      >
        Zu den Produkten
      </LinkButton>
      <GetAppContainer
        class="mi-home-app__app-icons"
        link-apple="https://apps.apple.com/de/app/xiaomi-home/id957323480"
        link-google="https://play.google.com/store/apps/details?id=com.xiaomi.smarthome"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { Heading, Paragraph, LinkButton } from '@i22-td-smarthome/component-library';
import CdnImage from '@/components/cdn-image.vue';
import GetAppContainer from '@/components/get-app-container.vue';

const content = {
  heading: 'Entdecke die welt von Xiaomi mit der Mi Home App',
  copy: 'Mit der Mi Home App hast du die Kontrolle über dein gesamtes Xiaomi-Ökosystem. Verbinde deine Smartphones, Tablets, Wearables, smarte Haushaltsgeräte und mehr nahtlos miteinander und genieße die Vorteile einer vollständig integrierten Technologieplattform. Entdecke die grenzenlosen Möglichkeiten von Xiaomi und erlebe die Macht der Interkonnektivität.',
  iconSpread: {
    mobile:
      '/content/content-images/landingpages/xiaomi/3103c3a676d6cc7f941a5bcbcea38716/mobile-spread.svg',
    desktop:
      '/content/content-images/landingpages/xiaomi/e26ee858a8f6140c1091391905d64585/desktop-spread.svg',
  },
  miHomeIcon:
    '/content/content-images/landingpages/xiaomi/icons/01c43b03fb886e4bd73f43c767343142/mi-home-app@2x.png',
  icons: [
    {
      src: '/content/content-images/landingpages/xiaomi/icons/13d462455f05e673794cd1a6aad63530/icon-tablets-smartphones.svg',
      copy: 'Smartphones/ Tablets',
      id: 'tablet',
    },
    {
      src: '/content/content-images/landingpages/xiaomi/icons/28755a2530642b6d51214a24c175537b/icon-watches.svg',
      copy: 'Smarte Wearables',
      id: 'watch',
    },
    {
      src: '/content/content-images/landingpages/xiaomi/icons/a848cbb58758daa82c3e08a21681718b/icon-scooter.svg',
      copy: 'Smarte Mobilität',
      id: 'scooter',
    },
    {
      src: '/content/content-images/landingpages/xiaomi/icons/d87a6d7007e9266bbf2db32383f8682d/icon-entertainment.svg',
      copy: 'Entertainment-Gadgets',
      id: 'tv',
    },
    {
      src: '/content/content-images/landingpages/xiaomi/icons/8ec92f3a4b4ef8d2c259841ea2bc6a90/icon-kuchengeraete.svg',
      copy: 'Smarte Küchenhelfer',
      id: 'kitchen',
    },
    {
      src: '/content/content-images/landingpages/xiaomi/icons/0eb7abe7db253b90869c2622698509f7/icon-haushaltsgeraete.svg',
      copy: 'Smarte Haushaltsgeräte',
      id: 'hoover',
    },
  ],
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.mi-home-app {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: $grid-base 0 40px;

  &__heading,
  &__copy {
    padding-inline: $grid-base;
  }

  &__copy {
    max-width: 1046px;
    margin-inline: auto;
  }

  &__icon-spread {
    &--mobile {
      @include mq($mq-sm) {
        display: none;
      }
    }

    &--desktop {
      @include mq(false, $mq-sm - 1) {
        display: none;
      }
    }
  }

  &__icons-container {
    display: grid;
    position: relative;
    width: 100%;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto;
    padding-top: 155px;
    grid-row-gap: 64px;
    margin-inline: auto;

    @include mq($mq-sm) {
      grid-template-columns: repeat(6, 12%);
      padding-inline: 2 * $grid-base;
      grid-column-gap: 5%;
      padding-top: 175px;
    }

    @include mq($mq-md) {
      padding-top: 195px;
    }

    @include mq($mq-lg) {
      padding-top: 215px;
    }
  }

  &__icon {
    &--tablet {
      width: 115px;
      height: 88px;

      @include mq($mq-sm, $mq-md - 1) {
        width: 71px;
        height: 54px;
      }
    }

    &--watch {
      width: 59px;
      height: 67px;
    }

    &--scooter {
      width: 109px;
      height: 100px;

      @include mq($mq-sm, $mq-md - 1) {
        width: 71px;
        height: 65px;
      }
    }

    &--tv {
      width: 115px;
      height: 72px;

      @include mq($mq-sm, $mq-md - 1) {
        width: 71px;
        height: 44px;
      }
    }

    &--kitchen {
      width: 75px;
      height: 87px;

      @include mq($mq-sm, $mq-md - 1) {
        width: 71px;
        height: 82px;
      }
    }

    &--hoover {
      width: 103px;
      height: 43px;

      @include mq($mq-sm, $mq-md - 1) {
        width: 71px;
        height: 30px;
      }
    }

    &-spread {
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      top: 70px;

      @include mq($mq-sm) {
        top: 109px;
        width: 80%;
      }
    }

    &-wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-end;
      gap: 12px;

      div {
        text-align: center;

        @include mq($mq-sm, 799px) {
          @include font-size(16px, 21px);
        }
      }
    }
  }

  &__mi-home-icon {
    width: 90px;
    height: 90px;
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);

    @include mq($mq-sm) {
      width: 120px;
      height: 120px;
    }
  }

  &__link {
    margin-top: 2 * $grid-base;

    @include mq($mq-md) {
      margin-top: 4 * $grid-base;
    }
  }

  &__icons-spread-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    width: 100%;
    max-width: 520px;

    @include mq($mq-sm) {
      max-width: 1500px;
    }
  }

  &__app-icons {
    @include mq(false, $mq-sm - 1) {
      flex-direction: column;
      gap: $grid-base;

      :deep(.get-app__link--ios) {
        margin-right: 0;
      }
    }
    @include mq(800px) {
      position: absolute;
      top: 73px;
      width: fit-content;
      left: calc(50% + 70px);
      margin-top: 0;
    }
  }
}
</style>
